import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useEffectOnce } from 'react-use'
import { observer } from 'mobx-react-lite'
import * as Dialog from '@radix-ui/react-dialog'

import { useUsername } from '@jotta/grpc-connect-client/customer'
import { useDisableDocumentScroll } from '@jotta/hooks'
import { AppLayoutPortal } from '@jotta/ui/AppLayoutPortal'
import { Search } from '@jotta/ui/Search'

import { useSearchStore, useSelectionStore } from '../../store/PhotoContext'
import {
  PhotoSelectionContext,
  usePhotoSearchSelectionStore,
} from '../../store/SelectionStore'
import { MediaCarouselContainer } from '../MediaCarousel/MediaCarouselContainer'
import { PhotoSelectionActions } from '../PhotoActions/PhotoSelectionActions'
import { timelineRef } from '../PhotoGallery/timelinev2/useTimelineStream'
import { SelectionManager } from '../SelectionManager/SelectionManager'

import { PhotoSearchThumbList } from './PhotoSearchThumbList'

export const PhotoSearchOverlay = observer(function PhotoSearchOverlay() {
  const [params, setParams] = useSearchParams()
  const q = params.get('q')
  const photoSearchSelectionStore = usePhotoSearchSelectionStore()
  const { clear: clearMainSelection } = useSelectionStore()
  const searchStore = useSearchStore()
  const [query, setQuery] = useState(q || '')
  const [mediaId, setMediaId] = useState<string | undefined>()
  const [forceRetry, setForceRetry] = useState(false)
  const username = useUsername()

  useDisableDocumentScroll({ enabled: searchStore.open })

  useEffectOnce(() => {
    if (params.has('q') || params.has('id')) {
      searchStore.open = true
    }
  })

  const [open, setOpen] = [
    searchStore.open,
    useCallback(
      (open: boolean) => {
        searchStore.open = open
        if (open) {
          clearMainSelection()
        }
      },
      [searchStore, clearMainSelection],
    ),
  ]

  useEffect(() => {
    if (forceRetry) {
      setForceRetry(false)
      searchStore.clear()
      return
    }

    const id = params.get('id')
    setMediaId(params.get('photo') || undefined)

    if (id) {
      searchStore.findSimilarPhotos(id)
      setQuery('')
      setOpen(true)
    } else if (q) {
      searchStore.search(q)
      setQuery(q)
      setOpen(true)
    } else {
      setQuery('')
      searchStore.clear()
      setOpen(false)
    }
  }, [searchStore, params, forceRetry, setOpen])

  const submit = (query: string) => {
    photoSearchSelectionStore.clear()

    if (
      searchStore.result &&
      searchStore.result.query === query &&
      !searchStore.result.isLoading &&
      !searchStore.result.isSuccess
    ) {
      setForceRetry(true)
    } else {
      const params = new URLSearchParams()
      params.set('q', query)
      setParams(params, { replace: false })
      setOpen(true)
    }
  }

  const onSubmit = (value: string) => {
    submit(value)
  }

  const onNavigate = useCallback(
    (id: string) => {
      params.set('photo', id)
      setParams(params)
    },
    [params, setParams],
  )

  const carouselIds = useMemo(() => {
    if (!searchStore.result) {
      return []
    }
    const photos = searchStore.result.photos || []

    if (searchStore.result.similarPhoto) {
      photos.unshift(searchStore.result.similarPhoto)
    }

    return photos.map(photo => photo.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchStore,
    searchStore.result?.similarPhoto,
    searchStore.result?.photos,
  ])

  return (
    <Dialog.Root modal={false} open>
      <Dialog.Portal>
        <Dialog.Content
          asChild
          onPointerDownOutside={e => {
            e.preventDefault()
          }}
          onInteractOutside={e => {
            e.preventDefault()
          }}
          onEscapeKeyDown={e => {
            if (searchStore.open && photoSearchSelectionStore.size === 0) {
              e.preventDefault()
              params.delete('q')
              setParams(params)
              setOpen(false)
              setQuery('')
            }
          }}
        >
          <PhotoSelectionContext.Provider value={usePhotoSearchSelectionStore}>
            <AppLayoutPortal slot="header">
              <Search
                type="photos"
                onOpenChange={active => {
                  searchStore.open = active
                }}
                onSubmit={onSubmit}
                initialQuery={query}
              />
            </AppLayoutPortal>
            {open && (
              <AppLayoutPortal slot="overlay">
                <PhotoSearchThumbList
                  result={searchStore.result}
                  onClick={onNavigate}
                />
              </AppLayoutPortal>
            )}
            {open && (
              <SelectionManager
                actionContext={'SMART_PHOTO_SEARCH'}
                selectables={searchStore.result?.photos}
              >
                <PhotoSelectionActions collectionRef={timelineRef} />
              </SelectionManager>
            )}
            <MediaCarouselContainer
              collectionRef={timelineRef}
              mediaId={mediaId}
              ids={carouselIds}
              showSimilarSearch
              navOnQueryParam="photo"
            />
          </PhotoSelectionContext.Provider>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
})
